import React from 'react'
import styled from '@emotion/styled'
import XLSX from 'xlsx'
import at from 'lodash/at'
import range from 'lodash/range'
import { Uploader, Button, spacer } from 'stylist'
import MultiSelect from './MultiSelect'
import { Slate, Red, Bold } from 'components/Text'
import FormMultiField from 'components/FormMultiField'

const ZIP_HEADERS = ['zip', 'zipcode', 'zip code']

const Wrapper = styled.div`
  text-align: center;
  max-width: 250px;
  margin: 0 auto;

  & > * {
    margin-bottom: ${spacer(5)};
  }
`

class ZipCode extends React.Component {
  state = {
    file: null,
    zips: null,
    loading: false,
    error: null
  }

  /**
   * Returns a list of valid zips in a given column
   */
  xlsxToZips = (sheetData, zipColumnIndex) => {
    const filterNonNumerics = val => String(val).replace(/\D/g, '')
    const firstFive = str => str.substring(0, 5).padStart(5, '0')

    return Array.from(
      sheetData.reduce((zipSet, row) => {
        const cell = row[zipColumnIndex]
        if (!cell) {
          return zipSet
        }
        const numerics = filterNonNumerics(row[zipColumnIndex])
        if (numerics.length === 0) {
          return zipSet
        }
        zipSet.add(firstFive(numerics))
        return zipSet
      }, new Set())
    )
  }

  /**
   * Takes a FileReader result and returns a list of zips or an error
   */
  readerResultToZips = (result, readAsBinary) => {
    const wb = XLSX.read(result, {
      type: readAsBinary ? 'binary' : 'array'
    })
    const sheet = wb.Sheets[wb.SheetNames[0]]
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 })
    const headers = data.shift()

    if (headers === undefined) {
      return [null, 'No rows found']
    }

    const zipIndex = headers.findIndex(
      key => key && ZIP_HEADERS.includes(String(key).toLowerCase())
    )

    if (zipIndex === -1) {
      return [
        null,
        `No zip code column found. Column must be named one of: ${ZIP_HEADERS}`
      ]
    }

    const zips = this.xlsxToZips(data, zipIndex)
    if (zips.length === 0) {
      return [
        null,
        `No valid zip codes found in the following column: ${headers[zipIndex]}`
      ]
    }

    return [zips, null]
  }

  /**
   * Validates a file and updates the component state
   */
  handleFile = file => {
    this.setState({ loading: true }, () => {
      const reader = new FileReader()
      const readAsBinary = !!reader.readAsBinaryString

      reader.onload = e => {
        const [zips, error] = this.readerResultToZips(
          e.target.result,
          readAsBinary
        )
        this.setState({ zips, error, file, loading: false })
      }

      if (readAsBinary) {
        reader.readAsBinaryString(file)
      } else {
        reader.readAsArrayBuffer(file)
      }
    })
  }

  /**
   * Uploads the file's zips into the main Form
   */
  uploadFileToForm = fileSetter => () => {
    const { onFileUpload } = this.props
    this.setState({ loading: true }, () => {
      fileSetter(this.state.zips)
      onFileUpload(this.state.zips) // Select all
      this.setState({ loading: false })
    })
  }

  setSelectedZips = setter => selected => {
    this.setState({ indexes: selected })
    setter(at(this.state.zips, selected))
  }

  render() {
    return (
      <FormMultiField names={['zipFile', 'questions.zip']}>
        {({ zipFile, 'questions.zip': selectedZips }) => {
          if (zipFile.value.length) {
            return (
              <MultiSelect
                name="zip"
                options={zipFile.value}
                selected={selectedZips.value.map(zip =>
                  zipFile.value.indexOf(zip)
                )}
                setSelected={idxs =>
                  selectedZips.setValue(idxs.map(idx => zipFile.value[idx]))
                }
              />
            )
          }
          return (
            <Wrapper>
              <Uploader
                accept=".xls,.xlsx,.csv"
                onChange={file => this.handleFile(file)}
                value={this.state.file}
                valid={!this.state.error}
              />
              {this.state.error ? (
                <div>
                  <Red>
                    <Bold>Error: {this.state.error}</Bold>
                  </Red>
                </div>
              ) : null}
              <div>
                <Slate>Accepted Files: .xls, .xlsx, .csv</Slate>
              </div>
              <Button
                kind="primary"
                type="button"
                disabled={
                  !this.state.zips || this.state.error || this.state.loading
                }
                onClick={this.uploadFileToForm(zipFile.setValue)}
              >
                Upload File
              </Button>
            </Wrapper>
          )
        }}
      </FormMultiField>
    )
  }
}

export default ZipCode



// WEBPACK FOOTER //
// ./src/routes/estimate/subroutes/form/ZipCode.js